import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  notification,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const getAuthHeaders = () => {
  const token = window.localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { authorization: `Token ${token}` } : {}),
  };
};

const pickArray = (json) => {
  if (!json || typeof json !== "object") return [];
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.subscribers)) return json.subscribers;
  if (Array.isArray(json.reportSubscribers)) return json.reportSubscribers;
  if (Array.isArray(json.items)) return json.items;
  if (json.data && Array.isArray(json.data.items)) return json.data.items;
  if (json.data && Array.isArray(json.data.subscribers)) return json.data.subscribers;
  return [];
};

const pickTotal = (json) => {
  if (!json || typeof json !== "object") return undefined;
  const candidates = [
    json.total,
    json.count,
    json.totalCount,
    json.data?.total,
    json.data?.count,
    json.data?.totalCount,
    json.data?.pagination?.total,
  ];
  return candidates.find((x) => typeof x === "number" && Number.isFinite(x));
};

const pickMessage = (json) =>
  json?.message || json?.msg || json?.error || json?.data?.message;

const normalizeRow = (s, idx) => {
  const id = s?._id || s?.id || s?.reportSubscriberId || s?.subscriberId || `${idx}`;
  return {
    key: id,
    _id: s?._id || s?.id || s?.reportSubscriberId || s?.subscriberId,
    name: s?.name || "",
    mobileNumber: s?.mobileNumber || s?.mobile || s?.phone || "",
    _raw: s,
  };
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(undefined);
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editTarget, setEditTarget] = useState(null);

  const fetchSubscribers = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;

      const params = new URLSearchParams();
      params.set("page", String(nextPage));
      params.set("limit", String(nextLimit));
      if (nextSearch && nextSearch.trim()) params.set("search", nextSearch.trim());

      setLoading(true);
      try {
        const resp = await fetch(
          `${apiBaseUrl}/inventoryManagement/listReportSubscribers?${params.toString()}`,
          {
            method: "GET",
            credentials: "include",
            headers: getAuthHeaders(),
          }
        );
        const json = await resp.json().catch(() => ({}));

        if (!resp.ok || json?.success === false) {
          throw new Error(pickMessage(json) || "Failed to fetch subscribers");
        }

        const list = pickArray(json);
        const normalized = list.map(normalizeRow);
        setRows(normalized);

        const t = pickTotal(json);
        setTotal(t);
      } catch (e) {
        setRows([]);
        setTotal(undefined);
        notification.error({
          message: "Failed to load subscribers",
          description: e?.message || "Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [limit, page, search]
  );

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        align: "center",
        render: (v) => <Text>{v || "-"}</Text>,
      },
      {
        title: "Mobile Number",
        dataIndex: "mobileNumber",
        key: "mobileNumber",
        align: "center",
        render: (v) => <Text>{v || "-"}</Text>,
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        width: 110,
        render: (_, record) => (
          <Space size={6}>
            <Tooltip title="Edit">
              <Button
                type="text"
                shape="circle"
                icon={<EditOutlined style={{ fontSize: 16 }} />}
                onClick={() => {
                  setEditTarget(record);
                  editForm.setFieldsValue({
                    reportSubscriberId: record._id,
                    name: record.name,
                    mobileNumber: record.mobileNumber,
                  });
                  setEditOpen(true);
                }}
                disabled={!record?._id}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                shape="circle"
                icon={<DeleteOutlined style={{ fontSize: 16 }} />}
                loading={deletingId === record._id}
                onClick={() => {
                  const id = record?._id;
                  if (!id) return;
                  Modal.confirm({
                    title: "Delete subscriber",
                    content: "Are you sure you want to delete this subscriber?",
                    okText: "Delete",
                    okType: "danger",
                    cancelText: "Cancel",
                    onOk: async () => {
                      setDeletingId(id);
                      try {
                        const resp = await fetch(
                          `${apiBaseUrl}/inventoryManagement/deleteReportSubscriber?id=${encodeURIComponent(
                            id
                          )}`,
                          {
                            method: "DELETE",
                            credentials: "include",
                            headers: getAuthHeaders(),
                          }
                        );
                        const json = await resp.json().catch(() => ({}));
                        if (!resp.ok || json?.success === false) {
                          throw new Error(
                            pickMessage(json) || "Failed to delete subscriber"
                          );
                        }
                        notification.success({
                          message: "Subscriber deleted",
                          description: pickMessage(json) || undefined,
                        });
                        await fetchSubscribers({ page: 1 });
                        setPage(1);
                      } catch (e) {
                        notification.error({
                          message: "Delete failed",
                          description: e?.message || "Please try again.",
                        });
                      } finally {
                        setDeletingId(null);
                      }
                    },
                  });
                }}
                disabled={!record?._id}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [deletingId, editForm, fetchSubscribers]
  );

  const pagination = useMemo(
    () => ({
      current: page,
      pageSize: limit,
      total: typeof total === "number" ? total : undefined,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      showTotal:
        typeof total === "number"
          ? (t) => `Total ${t} subscribers`
          : undefined,
    }),
    [limit, page, total]
  );

  const handleTableChange = async (nextPagination) => {
    const nextPage = nextPagination?.current ?? 1;
    const nextLimit = nextPagination?.pageSize ?? limit;
    setPage(nextPage);
    setLimit(nextLimit);
    await fetchSubscribers({ page: nextPage, limit: nextLimit });
  };

  const createSubscriber = async (values) => {
    setSaving(true);
    try {
      const resp = await fetch(
        `${apiBaseUrl}/inventoryManagement/createReportSubscriber`,
        {
          method: "POST",
          credentials: "include",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: values.name?.trim(),
            mobileNumber: values.mobileNumber?.trim(),
          }),
        }
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.success === false) {
        throw new Error(pickMessage(json) || "Failed to create subscriber");
      }

      notification.success({
        message: "Subscriber created",
        description: pickMessage(json) || undefined,
      });
      setCreateOpen(false);
      createForm.resetFields();
      setPage(1);
      await fetchSubscribers({ page: 1 });
    } catch (e) {
      notification.error({
        message: "Create failed",
        description: e?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateSubscriber = async (values) => {
    setSaving(true);
    try {
      const reportSubscriberId =
        values.reportSubscriberId || editTarget?._id || editTarget?.key;

      const resp = await fetch(
        `${apiBaseUrl}/inventoryManagement/updateReportSubscriber`,
        {
          method: "PUT",
          credentials: "include",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            reportSubscriberId,
            name: values.name?.trim(),
            mobileNumber: values.mobileNumber?.trim(),
          }),
        }
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.success === false) {
        throw new Error(pickMessage(json) || "Failed to update subscriber");
      }

      notification.success({
        message: "Subscriber updated",
        description: pickMessage(json) || undefined,
      });
      setEditOpen(false);
      setEditTarget(null);
      editForm.resetFields();
      await fetchSubscribers();
    } catch (e) {
      notification.error({
        message: "Update failed",
        description: e?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            textAlign: "center",
          }}
        />
      ),
    },
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          Monthly Report Subscribers
        </Title>
        <div style={{ flex: 1 }} />
        <Input
          allowClear
          placeholder="Search by name or mobile number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            setPage(1);
            fetchSubscribers({ page: 1, search });
          }}
          prefix={<SearchOutlined />}
          style={{ maxWidth: 360 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Add Subscriber
        </Button>
      </div>

      <div style={{ marginTop: 16 }}>
        <Table
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={pagination}
          onChange={handleTableChange}
          components={components}
        />
      </div>

      <Modal
        title="Add Subscriber"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        okText="Create"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={createSubscriber}
          preserve={false}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder="e.g. Rahul" />
          </Form.Item>
          <Form.Item
            name="mobileNumber"
            label="Mobile Number"
            rules={[
              { required: true, message: "Please enter mobile number" },
              { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
            ]}
          >
            <Input placeholder="e.g. 9876543210" maxLength={10} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Subscriber"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditTarget(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        okText="Update"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={updateSubscriber}
          preserve={false}
        >
          <Form.Item name="reportSubscriberId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder="e.g. Rahul Kumar" />
          </Form.Item>
          <Form.Item
            name="mobileNumber"
            label="Mobile Number"
            rules={[
              { required: true, message: "Please enter mobile number" },
              { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
            ]}
          >
            <Input placeholder="e.g. 9999999999" maxLength={10} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Reports;
